import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, Check, X, Trash2 } from 'lucide-react';
import useSavedViews from '@/hooks/useSavedViews';

/**
 * SavedViewsControl — glass-styled map control for saving / restoring named map views.
 *
 * @param {{
 *   mapRef: React.RefObject,
 *   statusFilter: string|null,
 *   groupFilter: string|number|null,
 *   onRestoreView: (view: {statusFilter, groupFilter}) => void,
 * }} props
 */
export default function SavedViewsControl({ mapRef, statusFilter, groupFilter, onRestoreView }) {
  const { t } = useTranslation();
  const { views, loading, saveView, deleteView, isFull } = useSavedViews();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSaving(false);
        setName('');
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  // Focus input when entering save mode
  useEffect(() => {
    if (saving && inputRef.current) {
      inputRef.current.focus();
    }
  }, [saving]);

  const handleRestore = useCallback(
    (view) => {
      const map = mapRef?.current;
      if (map) {
        map.flyTo({ center: view.center, zoom: view.zoom });
      }
      if (onRestoreView) {
        onRestoreView({ statusFilter: view.statusFilter, groupFilter: view.groupFilter });
      }
      setOpen(false);
    },
    [mapRef, onRestoreView],
  );

  const handleSave = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const map = mapRef?.current;
    const center = map ? [map.getCenter().lng, map.getCenter().lat] : [0, 0];
    const zoom = map ? map.getZoom() : 10;

    await saveView({ name: trimmed, center, zoom, statusFilter, groupFilter });
    setName('');
    setSaving(false);
  }, [name, mapRef, saveView, statusFilter, groupFilter]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        setSaving(false);
        setName('');
      }
    },
    [handleSave],
  );

  return (
    <div ref={containerRef} className="relative">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (open) {
            setSaving(false);
            setName('');
          }
        }}
        className="h-9 px-3.5 text-xs font-semibold rounded-xl flex items-center gap-2 transition-all shadow-md bg-white/90 text-slate-600 hover:bg-white border border-slate-200/80 dark:bg-slate-800/90 dark:text-slate-300 dark:border-slate-700/80 dark:hover:bg-slate-800"
        style={{ backdropFilter: 'blur(12px)' }}
        title={t('savedViews.title', 'Saved Views')}
      >
        <Bookmark className="w-4 h-4" />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute top-full mt-2 end-0 z-50 w-64 rounded-xl shadow-lg overflow-hidden"
          style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--glass-border)',
          }}
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-slate-200/30 dark:border-slate-700/30">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {t('savedViews.title', 'Saved Views')}
            </span>
          </div>

          {/* Views list */}
          <div className="max-h-52 overflow-y-auto">
            {loading && (
              <div className="px-3 py-4 text-center text-sm text-slate-400">
                {t('common.loading', 'Loading...')}
              </div>
            )}

            {!loading && views.length === 0 && !saving && (
              <div className="px-3 py-4 text-center text-sm text-slate-400 dark:text-slate-500">
                {t('savedViews.empty', 'No saved views')}
              </div>
            )}

            {!loading &&
              views.map((view) => (
                <div
                  key={view.id}
                  className="group flex items-center gap-2 px-3 py-2 hover:bg-slate-100/50 dark:hover:bg-slate-700/30 cursor-pointer transition-colors"
                >
                  <button
                    type="button"
                    className="flex-1 text-start text-sm text-slate-700 dark:text-slate-200 truncate"
                    onClick={() => handleRestore(view)}
                  >
                    {view.name}
                  </button>
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteView(view.id);
                    }}
                    title={t('common.delete', 'Delete')}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
          </div>

          {/* Save section */}
          <div className="border-t border-slate-200/30 dark:border-slate-700/30 px-3 py-2">
            {saving ? (
              <div className="flex items-center gap-1.5">
                <input
                  ref={inputRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('savedViews.namePlaceholder', 'View name...')}
                  maxLength={40}
                  className="flex-1 min-w-0 px-2 py-1 text-sm rounded-md bg-white/60 dark:bg-slate-800/60 border border-slate-300/40 dark:border-slate-600/40 text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:ring-1 focus:ring-blue-400"
                />
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!name.trim()}
                  className="p-1 rounded text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-30 transition-colors"
                  title={t('common.save', 'Save')}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSaving(false);
                    setName('');
                  }}
                  className="p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  title={t('common.cancel', 'Cancel')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={isFull}
                onClick={() => setSaving(true)}
                className="w-full text-start text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed py-1 transition-colors"
              >
                {isFull
                  ? t('savedViews.limitReached', 'Limit reached ({{max}})', { max: 10 })
                  : t('savedViews.saveCurrent', '+ Save current view')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
